import type { FleetUnit } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { getUnit } from '../units.api';

export type FleetUnitState =
  { status: 'loading' } | { status: 'loaded'; unit: FleetUnit } | { status: 'forbidden' } | { status: 'not_found' } | { status: 'error' };

function toErrorState(err: unknown): FleetUnitState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: 'error' };
}

/** The unit an address's registration names, re-read after a couple, swap or drop so the page follows the trailer. */
export function useFleetUnit(registration: string) {
  const [state, setState] = useState<FleetUnitState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    getUnit(registration)
      .then((unit) => {
        if (!cancelled) setState({ status: 'loaded', unit });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [registration]);

  /** Keeps the page up while it re-reads: the unit's tabs stay mounted through a swap. */
  const reload = useCallback(async () => {
    try {
      setState({ status: 'loaded', unit: await getUnit(registration) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }, [registration]);

  return { state, reload };
}
